import {
  ReadonlyBaseballBallToken,
  ReadonlyBaseballPlayerToken,
  ReadonlyBasketballPlayerToken,
  ReadonlyConeToken,
  ReadonlyCricketBallToken,
  ReadonlyCricketPlayerToken,
  ReadonlyCustomPlayerToken,
  ReadonlyFlagToken,
  ReadonlyHoopToken,
  ReadonlyHurdleToken,
  ReadonlyIceHockeyPlayerToken,
  ReadonlyPlainColorToken,
  ReadonlyPoleToken,
  ReadonlyRugbyPlayerToken,
  ReadonlySoccerBallToken,
  ReadonlySoccerPlayerToken,
  ReadonlyToken,
  ReadonlyVolleyballPlayerToken,
  TokenType,
  TokenVisitor,
} from "@sportactics/board-editing";

import { convertColorToDocument } from "../color-document";
import { convertPositionToDocument } from "../position-document";
import { BaseTokenDocument, TokenDocument } from "./token-document";

export function convertTokenToDocument(token: ReadonlyToken): TokenDocument {
  return TokenDocumentConverter.getInstance().visit(token);
}

class TokenDocumentConverter extends TokenVisitor<TokenDocument> {
  private static readonly instance = new TokenDocumentConverter();

  protected visitVolleyballBall(token: ReadonlyToken): TokenDocument {
    return {
      type: TokenType.VolleyballBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitVolleyballPlayer(
    token: ReadonlyVolleyballPlayerToken,
  ): TokenDocument {
    return {
      type: TokenType.VolleyballPlayer,
      ...this.convertBaseToken(token),
      team: token.team,
      role: token.role,
    };
  }

  protected visitBasketballBall(token: ReadonlyToken): TokenDocument {
    return {
      type: TokenType.BasketballBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitBasketballPlayer(
    token: ReadonlyBasketballPlayerToken,
  ): TokenDocument {
    return {
      type: TokenType.BasketballPlayer,
      ...this.convertBaseToken(token),
      team: token.team,
      playerPosition: token.playerPosition,
    };
  }

  protected visitSoccerBall(token: ReadonlySoccerBallToken): TokenDocument {
    return {
      type: TokenType.SoccerBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitSoccerPlayer(token: ReadonlySoccerPlayerToken): TokenDocument {
    return {
      type: TokenType.SoccerPlayer,
      team: token.team,
      playerPosition: token.playerPosition,
      ...this.convertBaseToken(token),
    };
  }

  protected visitIceHockeyPuck(token: ReadonlyToken): TokenDocument {
    return {
      type: TokenType.IceHockeyPuck,
      ...this.convertBaseToken(token),
    };
  }

  protected visitIceHockeyPlayer(
    token: ReadonlyIceHockeyPlayerToken,
  ): TokenDocument {
    return {
      type: TokenType.IceHockeyPlayer,
      team: token.team,
      playerPosition: token.playerPosition,
      ...this.convertBaseToken(token),
    };
  }

  protected visitRugbyBall(token: ReadonlyToken): TokenDocument {
    return {
      type: TokenType.RugbyBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitRugbyPlayer(token: ReadonlyRugbyPlayerToken): TokenDocument {
    return {
      type: TokenType.RugbyPlayer,
      team: token.team,
      playerPosition: token.playerPosition,
      ...this.convertBaseToken(token),
    };
  }

  protected visitBaseballBall(token: ReadonlyBaseballBallToken): TokenDocument {
    return {
      type: TokenType.BaseballBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitBaseballPlayer(
    token: ReadonlyBaseballPlayerToken,
  ): TokenDocument {
    return {
      type: TokenType.BaseballPlayer,
      playerPosition: token.playerPosition,
      ...this.convertBaseToken(token),
    };
  }

  protected visitCustomPlayer(token: ReadonlyCustomPlayerToken): TokenDocument {
    return {
      type: TokenType.CustomPlayer,
      number: token.number,
      name: token.name,
      ...this.convertBaseToken(token),
    };
  }

  protected visitCone(token: ReadonlyConeToken): TokenDocument {
    return {
      type: TokenType.Cone,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  protected visitFlag(token: ReadonlyFlagToken): TokenDocument {
    return {
      type: TokenType.Flag,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  protected visitHoop(token: ReadonlyHoopToken): TokenDocument {
    return {
      type: TokenType.Hoop,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  protected visitCricketBall(token: ReadonlyCricketBallToken): TokenDocument {
    return {
      type: TokenType.CricketBall,
      ...this.convertBaseToken(token),
    };
  }

  protected visitCricketPlayer(
    token: ReadonlyCricketPlayerToken,
  ): TokenDocument {
    return {
      type: TokenType.CricketPlayer,
      team: token.team,
      ...this.convertBaseToken(token),
    };
  }

  protected visitHurdle(token: ReadonlyHurdleToken): TokenDocument {
    return {
      type: TokenType.Hurdle,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  protected visitPole(token: ReadonlyPoleToken): TokenDocument {
    return {
      type: TokenType.Pole,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  protected visitPlainColor(token: ReadonlyPlainColorToken): TokenDocument {
    return {
      type: TokenType.PlainColor,
      color: convertColorToDocument(token.color),
      ...this.convertBaseToken(token),
    };
  }

  private convertBaseToken(token: ReadonlyToken): BaseTokenDocument {
    return {
      version: 1,
      id: token.id,
      position: convertPositionToDocument(token.position),
    };
  }

  static getInstance(): TokenDocumentConverter {
    return this.instance;
  }
}
